import { Box, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import jsQR from "jsqr";
import { useAddSelfWash } from "lib/api/efc-log";
import AlertMessage from "components/utils/AlertMessage";
import { useNavigate } from "react-router-dom";
import { AppUrl } from "components/utils/constants/AppUrl";

const BoxCamera = styled(Box)({
	position: "relative",
	marginTop: 5,
	border: "1px solid black",
	borderRadius: 2,
	width: 300,
	height: 300,
});

const SelfWash = () => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const timeRecallAfterError = 2000;
	const navigation = useNavigate()

	const [modalAlert, setModalAlert] = useState<{
		isOpen: boolean;
		message: string | string[];
		type: "error" | "success" | "info" | "warning";
	}>({ isOpen: false, message: "", type: "success" });

	const handleCreateSelfWash = useAddSelfWash(
		(res) => {
			setModalAlert({
				isOpen: true,
				type: "success",
				message: res,
			});
			setTimeout(() => {
				navigation(AppUrl.MY_BOTTLE);
			}, 2000);
		},
		(err) => {
			setModalAlert({
				type: "error",
				isOpen: true,
				message: err?.response?.data?.message || "エラー",
			});
		}
	);

	useEffect(() => {
		const currentVideoRef = videoRef.current;
		const canvas = canvasRef.current;

		const scanQrCode = () => {
			if (canvas && currentVideoRef) {
				const ctx = canvas.getContext("2d");
				if (ctx) {
					// カメラの映像をcanvasに描画する
					ctx.drawImage(currentVideoRef, 0, 0, canvas.width, canvas.height);
					const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

					// 画像データを取得
					var data = imageData.data;

					// シャープメソッド
					const _data = data.slice();
					const sharpedColor = (color: number, i: number) => {
						// 係数
						const sub = -1;
						const main = 10;

						const prevLine = i - canvas.width * 4;
						const nextLine = i + canvas.width * 4;

						const sumPrevLineColor =
							_data[prevLine - 4 + color] * sub +
							_data[prevLine + color] * sub +
							_data[prevLine + 4 + color] * sub;
						const sumCurrLineColor =
							_data[i - 4 + color] * sub +
							_data[i + color] * main +
							_data[i + 4 + color] * sub;
						const sumNextLineColor =
							_data[nextLine - 4 + color] * sub +
							_data[nextLine + color] * sub +
							_data[nextLine + 4 + color] * sub;

						return (sumPrevLineColor + sumCurrLineColor + sumNextLineColor) / 2;
					};

					// 画像をシャープにする処理
					for (var i = 0; i < data.length; i += 4) {
						// 2列目〜n-1列目
						if (
							i % (canvas.width * 4) === 0 ||
							i % (canvas.width * 4 + 300) === 0
						) {
							// nop
						} else {
							data[i] = sharpedColor(0, i);
							data[i + 1] = sharpedColor(1, i);
							data[i + 2] = sharpedColor(2, i);
						}
					}

					// 変更した画像データをCanvasに描画
					ctx.putImageData(imageData, 0, 0);

					// QRコードをスキャンする
					const qrCodeData = jsQR(
						imageData.data,
						imageData.width,
						imageData.height,
						{ inversionAttempts: "attemptBoth" }
					);
					if (qrCodeData && qrCodeData.data) {
						handleCreateSelfWash.mutate(
							{
								code: qrCodeData.data,
							},
							{
								onError: () => {
									timeoutRef.current = setTimeout(scanQrCode, timeRecallAfterError);
								},
							}
						);
						return;
					} else {
						if (timeoutRef.current) clearTimeout(timeoutRef.current);
						timeoutRef.current = setTimeout(scanQrCode, 1000);
					}
				}
			}
		};
		const constraints = {
			video: {
				facingMode: "environment",
				width: { ideal: 300 },
				height: { ideal: 300 },
			},
		};

		navigator.mediaDevices
			.getUserMedia(constraints)
			.then((stream) => {
				if (currentVideoRef) {
					currentVideoRef.srcObject = stream;

					// loadedmetadata イベントを待つ関数
					const onLoadedMetadata = () => {
						// 再度確認してから再生
						if (currentVideoRef) {
							currentVideoRef
								.play()
								.then(() => {
									// 再生に成功したらQRコードのスキャンを開始
									scanQrCode();
								})
								.catch((playError) => {
									console.error("Error playing video:", playError);
								})
								.finally(() => {
									// イベントリスナーを削除
									currentVideoRef?.removeEventListener(
										"loadedmetadata",
										onLoadedMetadata
									);
								});
						} else {
							console.error("Error not find video ref");
						}
					};

					// loadedmetadata イベントリスナーを追加
					currentVideoRef.addEventListener("loadedmetadata", onLoadedMetadata);
				}
			})
			.catch((err) => console.error("Error accessing media devices:", err));

		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			if (currentVideoRef && currentVideoRef.srcObject) {
					const stream = currentVideoRef.srcObject as MediaStream;
					stream.getTracks().forEach((track) => track.stop());
					currentVideoRef.srcObject = null;
			}			if (currentVideoRef && currentVideoRef.srcObject) {
				const stream = currentVideoRef.srcObject as MediaStream;
				const tracks = stream.getTracks();
				tracks.forEach((track) => track.stop());
			}
		};
	}, []);

	return (
		<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
			<BoxCamera>
				<Box>
					<video
						ref={videoRef}
						muted
						playsInline
						style={{
							position: "absolute",
							left: 0,
							top: 0,
							zIndex: 50,
							height: "100%",
							width: "100%",
						}}
					/>
					<canvas
						ref={canvasRef}
						width="300"
						height="300"
						style={{ display: "none" }}
					/>
				</Box>
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display:
							handleCreateSelfWash.isPending || modalAlert.isOpen
								? "flex"
								: "none",
						justifyContent: "center",
						alignItems: "center",
						minHeight: "200px",
						position: "absolute",
					}}
				>
					<CircularProgress />
				</Box>
			</BoxCamera>

			<AlertMessage
				open={modalAlert.isOpen}
				setOpen={(open: boolean) =>
					setModalAlert({ isOpen: open, message: "", type: "warning" })
				}
				severity={modalAlert.type}
				message={modalAlert.message}
				autoHideDuration={2000}
			/>
		</Box>
	);
};

export default SelfWash;
